import {
  CardStyleInterpolators,
  StackScreenProps,
} from "@react-navigation/stack";
import { ProductActionsRootParamList } from "../../../../types";
import { ProductActionsScreen } from "../ProductActionsScreen";
import {
  ModalCloseButton,
  createStackHeaderNavigator,
} from "@towersystems/roam-common-native";
import { ProductActionsSelectParentProductScreen } from "../ProductActionsSelectParentProductScreen";
import { ProductActionsSelectParentOptionValuesScreen } from "../ProductActionsSelectParentOptionValuesScreen";
import { AddMainProductFormActionsContext } from "../../components/AddMainProductForm/context";
import { useAddMainProductFormActionsContextProvider } from "../../components/AddMainProductForm/hooks";
import { useFormContext } from "react-hook-form";
import {
  productEntityToFormData,
  timeout,
  useConvertMainProductFromTemplate,
  useCreateVariantForProduct,
  useDuplicateProduct,
} from "../../utils";
import { ProductActionsSelectVariantTemplateRootScreen } from "../ProductActionsSelectVariantTemplateRootScreen/ProductActionsSelectVariantTemplateRootScreen";
import { CreateVariantsFromTemplateFormContext } from "../../components/CreateVariantsFromTemplateForm/context";
import { CreateVariantsFromTemplateFormValues } from "../../components/CreateVariantsFromTemplateForm/types";
import { Product } from "@towersystems/roam-common/lib/generated-types";
import { ProductActionsCreateChildVariantScreen } from "../ProductActionsCreateChildVariantScreen";
import { CreateVariantChildFormFormContext } from "../../components/CreateVariantChildForm/context";
import { CreateVariantChildFormValues } from "../../components/CreateVariantChildForm/types";
import { useSetupAllStartupEntities } from "../../../../utilities/setup";
import { ProductActionsCopyProductScreen } from "../ProductActionsCopyProductScreen";
import { DuplicateProductFormContext } from "../../components/DuplicateProductForm/context";
import { DuplicateProductFormValues } from "../../components/DuplicateProductForm/types";
import { useDuplicateProductFormProvider } from "../../components/DuplicateProductForm/hooks";

export type ProductActionsRootScreenProps = StackScreenProps<
  any,
  "ProductActionsRootScreen"
>;

export const ProductActionsRootScreen = ({
  navigation,
}: ProductActionsRootScreenProps) => {
  const maybeActions = useAddMainProductFormActionsContextProvider();
  const { reset, getValues } = useFormContext();

  const maybeDuplicateProductContext = useDuplicateProductFormProvider();
  const RootNavigator =
    createStackHeaderNavigator<ProductActionsRootParamList>();

  const { handleConvertMainProductFromTemplate } =
    useConvertMainProductFromTemplate();

  const { fetchAndSyncEntity } = useSetupAllStartupEntities();
  const { handleDuplicateProduct } = useDuplicateProduct();

  const { handleCreateVariantForProduct } = useCreateVariantForProduct();
  return (
    <DuplicateProductFormContext.Provider
      value={{
        handleOnSubmit: async (input: DuplicateProductFormValues) => {
          // replace the current navigation stack with the newly created product
          // modify this so parent provides context if needs changed
          await handleDuplicateProduct(input, {
            successCallback: async (product) => {
              await maybeDuplicateProductContext?.handleOnSubmit(input);
              navigation.replace("EditProductRootScreen", {
                productId: product.id,
              });
            },
          });
        },
      }}
    >
      <CreateVariantChildFormFormContext.Provider
        value={{
          handleOnSubmit: async (input: CreateVariantChildFormValues) => {
            await handleCreateVariantForProduct(input, {
              successCallback: async () => {
                await fetchAndSyncEntity("products");
                navigation.pop();
              },
            });
          },
        }}
      >
        <CreateVariantsFromTemplateFormContext.Provider
          value={{
            handleOnSubmit: async (
              input: CreateVariantsFromTemplateFormValues
            ) => {
              await handleConvertMainProductFromTemplate(input, {
                successCallback: async (product: Product) => {
                  const locationId = getValues("locationId");
                  reset(productEntityToFormData(product, locationId), {
                    keepDirtyValues: true,
                  });
                  await timeout(100); //for 1 sec delay
                  navigation.pop();
                },
              });
            },
          }}
        >
          <AddMainProductFormActionsContext.Provider
            value={{
              onAfterSubmitSuccess: async (product) => {
                const locationId = getValues("locationId");
                reset(productEntityToFormData(product, locationId), {
                  keepDirtyValues: true,
                });
                await maybeActions?.onAfterSubmitSuccess?.(product);
                await timeout(100); //for 1 sec delay
                navigation.pop();
              },
            }}
          >
            <RootNavigator.Navigator
              screenOptions={{
                headerShown: true,
                headerLeft: () => <ModalCloseButton type="Close" />,
                animationEnabled: true,
                cardStyleInterpolator: CardStyleInterpolators.forHorizontalIOS,
                title: "",
              }}
            >
              <RootNavigator.Screen
                name="ProductActionsScreen"
                component={ProductActionsScreen}
              />
              <RootNavigator.Screen
                name="ProductActionsSelectParentProductScreen"
                component={ProductActionsSelectParentProductScreen}
              />
              <RootNavigator.Screen
                name="ProductActionsSelectParentOptionValuesScreen"
                component={ProductActionsSelectParentOptionValuesScreen}
              />
              <RootNavigator.Screen
                name="ProductActionsSelectVariantTemplateRootScreen"
                component={ProductActionsSelectVariantTemplateRootScreen}
                options={{
                  headerShown: false,
                }}
              />
              <RootNavigator.Screen
                name="ProductActionsCreateChildVariantScreen"
                component={ProductActionsCreateChildVariantScreen}
              />
              <RootNavigator.Screen
                name="ProductActionsCopyProductScreen"
                component={ProductActionsCopyProductScreen}
              />
            </RootNavigator.Navigator>
          </AddMainProductFormActionsContext.Provider>
        </CreateVariantsFromTemplateFormContext.Provider>
      </CreateVariantChildFormFormContext.Provider>
    </DuplicateProductFormContext.Provider>
  );
};
